import { createApi } from '@reduxjs/toolkit/query/react';
import type { FetchBaseQueryError } from '@reduxjs/toolkit/query';
import {
  UploadAttachmentResponse,
  DownloadByUrlResponse,
  DownloadFileResponse,
  DeleteAttachmentResponse,
  AttachmentFile,
  UploadAttachmentArgs,
  UploadWaiverAttachmentArgs,
} from '../../types/attachments';
import { getUploadFailureStatus, uploadWithProgress } from '../../utilities/uploadWithProgress';
import { baseApiQuery, tryRefreshAuthToken } from '../basequery';
import type { COIStore } from '../store';

const getAuthToken = (getState: () => unknown): string | null => {
  const {
    auth: { token },
  } = getState() as COIStore;
  return token ?? null;
};

type UploadMutationApi = {
  getState: () => unknown;
  dispatch: Parameters<typeof tryRefreshAuthToken>[0]['dispatch'];
};

/**
 * Runs a multipart upload with progress.
 * On 401, refreshes the access token (same recovery as baseApiQuery) and retries once.
 * On failure, returns the API error payload as the mutation error so existing
 * component catch handlers (expecting UploadAttachmentError) keep working —
 * same behavior as the previous transformErrorResponse path.
 */
const runUploadMutation = async (
  path: string,
  { file, additionalData, onProgress }: Omit<UploadAttachmentArgs, 'FormID'>,
  api: UploadMutationApi
): Promise<{ data: UploadAttachmentResponse } | { error: FetchBaseQueryError }> => {
  const executeUpload = (token: string | null) =>
    uploadWithProgress({
      path,
      file,
      token,
      additionalData,
      onProgress,
    });

  const requestToken = getAuthToken(api.getState);

  try {
    const data = await executeUpload(requestToken);
    return { data };
  } catch (error) {
    if (getUploadFailureStatus(error) !== 401) {
      return { error: error as FetchBaseQueryError };
    }

    const refreshedAccessToken = await tryRefreshAuthToken(api, requestToken);
    if (!refreshedAccessToken) {
      return { error: error as FetchBaseQueryError };
    }

    try {
      const data = await executeUpload(refreshedAccessToken);
      return { data };
    } catch (retryError) {
      return { error: retryError as FetchBaseQueryError };
    }
  }
};

export const attachmentsApi = createApi({
  reducerPath: 'attachmentsApi',
  baseQuery: baseApiQuery,
  tagTypes: ['AttachmentsID'],
  endpoints: (builder) => ({
    /**
     * Uploads a file attachment with multipart form data and real upload progress.
     * @param FormID - The form ID to associate the attachment with
     * @param file - The file to upload
     * @param additionalData - Optional additional form data fields
     * @param onProgress - Optional 0-100 progress callback driven by bytes uploaded
     * @returns UploadAttachmentResponse on success
     * @throws UploadAttachmentError - Union type containing:
     *   - FileValidationErrorResponse when file validation fails
     *   - NoFilesSelectedErrorResponse when no file is selected
     */
    uploadAttachment: builder.mutation<UploadAttachmentResponse, UploadAttachmentArgs>({
      async queryFn({ FormID, file, additionalData, onProgress }, api) {
        return runUploadMutation(
          `/attachments/upload/${FormID}`,
          { file, additionalData, onProgress },
          api
        );
      },
      invalidatesTags: ['AttachmentsID'],
    }),
    /**
     * Uploads a search-results attachment with multipart form data and real upload progress.
     * @param FormID - The form ID to associate the attachment with
     * @param file - The file to upload
     * @param additionalData - Optional additional form data fields
     * @param onProgress - Optional 0-100 progress callback driven by bytes uploaded
     * @returns UploadAttachmentResponse on success
     */
    uploadSearchResultsAttachment: builder.mutation<UploadAttachmentResponse, UploadAttachmentArgs>(
      {
        async queryFn({ FormID, file, additionalData, onProgress }, api) {
          return runUploadMutation(
            `/attachments/uploadsearchresults/${FormID}`,
            { file, additionalData, onProgress },
            api
          );
        },
        invalidatesTags: ['AttachmentsID'],
      }
    ),
    /**
     * Retrieves attachments by FormID
     * @param FormID - The FormID to get attachments for
     * @returns Object with data containing Attachments array
     */
    getAttachmentsByFormID: builder.query<{ data: { Attachments: AttachmentFile[] } }, string>({
      async queryFn(FormID, api, extraOptions) {
        const result = await baseApiQuery(
          {
            url: `/attachments/getattachment/${FormID}`,
            method: 'GET',
          },
          api,
          extraOptions
        );

        if (result.error) {
          const status = (result.error as { status?: number | string }).status;
          if (status === 404) {
            return { data: { data: { Attachments: [] } } };
          }
          return { error: result.error };
        }

        return {
          data: result.data as { data: { Attachments: AttachmentFile[] } },
        };
      },
      providesTags: ['AttachmentsID'],
    }),
    /**
     * Retrieves search-results attachments by FormID
     * @param FormID - The FormID to get search-results attachments for
     * @returns Object with data containing Attachments array
     */
    getSearchResultsAttachmentsByFormID: builder.query<
      { data: { Attachments: AttachmentFile[] } },
      string
    >({
      async queryFn(FormID, api, extraOptions) {
        const result = await baseApiQuery(
          {
            url: `/attachments/searchresults/${FormID}`,
            method: 'GET',
          },
          api,
          extraOptions
        );

        if (result.error) {
          const status = (result.error as { status?: number | string }).status;
          if (status === 404) {
            return { data: { data: { Attachments: [] } } };
          }
          return { error: result.error };
        }

        return {
          data: result.data as { data: { Attachments: AttachmentFile[] } },
        };
      },
      providesTags: ['AttachmentsID'],
    }),
    /**
     * Downloads an attachment as binary content
     * @param fileAttachmentID - The FileAttachmentID of the attachment
     * @returns Blob with resolved filename/content-type from response headers
     */
    getDownload: builder.query<DownloadFileResponse, number>({
      query: (fileAttachmentID: number) => ({
        url: `/attachments/download/${fileAttachmentID}`,
        method: 'GET',
        responseHandler: async (response) => response.blob(),
      }),
      transformResponse: (blob: Blob, meta): DownloadFileResponse => {
        const headers = meta?.response?.headers;
        const contentType = headers?.get('content-type') || 'application/octet-stream';
        const contentDisposition = headers?.get('content-disposition') || '';

        const encodedFileNameMatch = contentDisposition.match(/filename\*\s*=\s*UTF-8''([^;]+)/i);
        const plainFileNameMatch = contentDisposition.match(/filename\s*=\s*"?([^";]+)"?/i);
        const rawFileName = encodedFileNameMatch?.[1] || plainFileNameMatch?.[1] || '';
        let fileName = rawFileName;

        if (fileName) {
          try {
            fileName = decodeURIComponent(fileName);
          } catch {
            fileName = rawFileName;
          }
        }

        return {
          blob,
          fileName,
          contentType,
        };
      },
      providesTags: ['AttachmentsID'],
    }),
    /**
     * Gets a pre-signed download URL for an attachment
     * @param fileAttachmentID - The FileAttachmentID of the attachment
     * @returns DownloadByUrlResponse with download URL and expiration info
     */
    getDownloadByUrl: builder.query<DownloadByUrlResponse, number>({
      query: (fileAttachmentID: number) => ({
        url: `/attachments/downloadbyurl/${fileAttachmentID}`,
        method: 'GET',
      }),
      providesTags: ['AttachmentsID'],
    }),
    /**
     * Gets a pre-signed download URL for a search-result attachment (no form-status check)
     * @param fileAttachmentID - The FileAttachmentID of the attachment
     * @returns DownloadByUrlResponse with download URL and expiration info
     */
    getSearchResultDownloadByUrl: builder.query<DownloadByUrlResponse, number>({
      query: (fileAttachmentID: number) => ({
        url: `/attachments/downloadsearchresultbyurl/${fileAttachmentID}`,
        method: 'GET',
      }),
      providesTags: ['AttachmentsID'],
    }),
    /**
     * Gets a pre-signed download URL for a waiver attachment
     * @param fileAttachmentID - The FileAttachmentID of the attachment
     * @returns DownloadByUrlResponse with download URL and expiration info
     */
    getWaiverDownloadByUrl: builder.query<DownloadByUrlResponse, number>({
      query: (fileAttachmentID: number) => ({
        url: `/attachments/downloadwaiverbyurl/${fileAttachmentID}`,
        method: 'GET',
      }),
      providesTags: ['AttachmentsID'],
    }),
    /**
     * Uploads a waiver attachment with multipart form data and real upload progress.
     * @param FormID - The form ID to associate the attachment with
     * @param file - The file to upload
     * @param onProgress - Optional 0-100 progress callback driven by bytes uploaded
     * @returns UploadAttachmentResponse on success
     */
    uploadWaiverAttachment: builder.mutation<UploadAttachmentResponse, UploadWaiverAttachmentArgs>({
      async queryFn({ FormID, file, onProgress }, api) {
        return runUploadMutation(`/attachments/uploadwaiver/${FormID}`, { file, onProgress }, api);
      },
      invalidatesTags: ['AttachmentsID'],
    }),
    /**
     * Retrieves waiver attachments by FormID
     * @param FormID - The FormID to get waiver attachments for
     * @returns Object with data containing Attachments array
     */
    getWaiverAttachmentsByFormID: builder.query<
      { data: { Attachments: AttachmentFile[] } },
      string
    >({
      async queryFn(FormID, api, extraOptions) {
        const result = await baseApiQuery(
          {
            url: `/attachments/waiver/${FormID}`,
            method: 'GET',
          },
          api,
          extraOptions
        );

        if (result.error) {
          const status = (result.error as { status?: number | string }).status;
          if (status === 404) {
            return { data: { data: { Attachments: [] } } };
          }
          return { error: result.error };
        }

        return {
          data: result.data as { data: { Attachments: AttachmentFile[] } },
        };
      },
      providesTags: ['AttachmentsID'],
    }),
    /**
     * Deletes a waiver attachment by FileAttachmentID
     * @param fileAttachmentID - The FileAttachmentID of the attachment to delete
     * @returns DeleteAttachmentResponse with success status, message, and timestamp
     */
    deleteWaiverAttachmentByFileAttachmentID: builder.mutation<DeleteAttachmentResponse, number>({
      query: (fileAttachmentID: number) => ({
        url: `/attachments/deletewaiver/${fileAttachmentID}`,
        method: 'DELETE',
      }),
      invalidatesTags: ['AttachmentsID'],
    }),
    /**
     * Deletes an attachment by FileAttachmentID
     * @param fileAttachmentID - The FileAttachmentID of the attachment to delete
     * @returns DeleteAttachmentResponse with success status, message, and timestamp
     */
    deleteAttachmentByFileAttachmentID: builder.mutation<DeleteAttachmentResponse, number>({
      query: (fileAttachmentID: number) => ({
        url: `/attachments/delete/${fileAttachmentID}`,
        method: 'DELETE',
      }),
      invalidatesTags: ['AttachmentsID'],
    }),
    /**
     * Deletes a search-result attachment by FileAttachmentID (no form-status check)
     * @param fileAttachmentID - The FileAttachmentID of the attachment to delete
     * @returns DeleteAttachmentResponse with success status, message, and timestamp
     */
    deleteSearchResultAttachmentByFileAttachmentID: builder.mutation<
      DeleteAttachmentResponse,
      number
    >({
      query: (fileAttachmentID: number) => ({
        url: `/attachments/deletesearchresult/${fileAttachmentID}`,
        method: 'DELETE',
      }),
      invalidatesTags: ['AttachmentsID'],
    }),
  }),
});

export const {
  useUploadAttachmentMutation,
  useUploadSearchResultsAttachmentMutation,
  useGetAttachmentsByFormIDQuery,
  useGetSearchResultsAttachmentsByFormIDQuery,
  useLazyGetDownloadQuery,
  useLazyGetDownloadByUrlQuery,
  useLazyGetSearchResultDownloadByUrlQuery,
  useLazyGetWaiverDownloadByUrlQuery,
  useLazyGetAttachmentsByFormIDQuery,
  useDeleteAttachmentByFileAttachmentIDMutation,
  useDeleteSearchResultAttachmentByFileAttachmentIDMutation,
  useUploadWaiverAttachmentMutation,
  useGetWaiverAttachmentsByFormIDQuery,
  useLazyGetWaiverAttachmentsByFormIDQuery,
  useDeleteWaiverAttachmentByFileAttachmentIDMutation,
} = attachmentsApi;
